import { createSlice } from "@reduxjs/toolkit"

import { generateSetPropReducer } from "../../../redux/shared/set-setate-prop.reducer.ts"
import {
  emailContractToClient,
  markContractAsSent,
  submitContractForReview,
  uploadContract
} from "../../Contracts/redux/contracts.thunks.ts"
import { logout } from "../../LoginPage/redux/auth.thunks.ts"
import {
  emailProposalToClient,
  markProposalAsSent,
  submitProposalForReview,
  uploadProposal
} from "../../Proposals/redux/proposals.thunks.ts"
import {
  createLead,
  deleteLead,
  fetchLead,
  toggleLeadFavorite,
  updateLead,
  updateLeadEvent
} from "./lead.thunks.ts"
import { type LeadState } from "./lead.types.ts"

// Initial State
const initialState: LeadState = {
  lead: null,
  notFound: false,
  loadFailed: false,

  fetching: false,
  savingEvent: false,
  saveResult: null,
  saveFetching: false
}

// Slice
const leadSlice = createSlice({
  name: "lead",
  initialState,
  reducers: {
    leadSetProp: generateSetPropReducer<LeadState>()
  },
  extraReducers: (builder) => {
    builder
      // createLead
      .addCase(createLead.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(createLead.fulfilled, (state) => {
        state.saveFetching = false
      })
      .addCase(createLead.rejected, (state) => {
        state.saveFetching = false
      })

      // updateLead
      .addCase(updateLead.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(updateLead.fulfilled, (state, action) => {
        state.saveFetching = false
        state.lead = {
          ...state.lead,
          ...action.payload
        }
      })
      .addCase(updateLead.rejected, (state) => {
        state.saveFetching = false
      })

      // toggleLeadFavorite
      .addCase(toggleLeadFavorite.fulfilled, (state, action) => {
        if (state.lead?.id === action.payload.id) {
          state.lead.is_favorite = action.payload.is_favorite
        }
      })

      // deleteLead
      .addCase(deleteLead.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(deleteLead.fulfilled, (state, action) => {
        state.saveFetching = false
      })
      .addCase(deleteLead.rejected, (state) => {
        state.saveFetching = false
      })

      // fetchLead
      .addCase(fetchLead.pending, (state, action) => {
        state.fetching = true
        state.notFound = false
        state.loadFailed = false
        // A re-read of the record already on screen keeps it: every page
        // re-fetches on mount, and blanking first turns one failed refresh
        // into a lead that cannot be reached again without a reload.
        if (state.lead?.id !== action.meta.arg) state.lead = null
      })
      .addCase(fetchLead.fulfilled, (state, action) => {
        state.fetching = false
        state.lead = action.payload ?? null
        state.notFound = !action.payload
      })
      .addCase(fetchLead.rejected, (state, action) => {
        state.fetching = false

        if (action.payload?.status === 404) {
          state.lead = null
          state.notFound = true
          return
        }

        state.loadFailed = true
      })

      // updateLeadEvent
      .addCase(updateLeadEvent.pending, (state) => {
        state.savingEvent = true
      })
      .addCase(updateLeadEvent.fulfilled, (state, action) => {
        const events = state.lead?.lead_calendar_events
        const updated = action.payload

        if (events && updated) {
          const index = events.findIndex((e) => e.id === updated.id)
          if (index !== -1) events[index] = updated
        }

        state.savingEvent = false
      })
      .addCase(updateLeadEvent.rejected, (state) => {
        state.savingEvent = false
      })

      // proposal send to submit
      .addCase(submitProposalForReview.fulfilled, (state, action) => {
        const updatedProposal = action.payload

        if (state.lead) {
          state.lead.proposals = state?.lead?.proposals?.map((p) =>
            p?.id === updatedProposal?.id ? updatedProposal : p
          )
        }
      })
      // todo MAKE UNIVERSAL REDUCERS TO REMOVE DUPLICATE CODE
      // upload proposal manually
      .addCase(uploadProposal.fulfilled, (state, action) => {
        if (state.lead) {
          state.lead.proposals = [
            action.payload,
            ...(state.lead.proposals || [])
          ]
        }
      })

      // upload proposal manually
      .addCase(uploadContract.fulfilled, (state, action) => {
        if (state.lead) {
          state.lead.contracts = [
            action.payload,
            ...(state.lead.contracts || [])
          ]
        }
      })

      // contract send to submit
      .addCase(submitContractForReview.fulfilled, (state, action) => {
        const updatedContract = action.payload

        if (state.lead) {
          state.lead.contracts = state?.lead?.contracts?.map((p) =>
            p?.id === updatedContract?.id ? updatedContract : p
          )
        }
      })

      // contract mark as sent
      .addCase(markContractAsSent.fulfilled, (state, action) => {
        const updatedContract = action.payload

        if (state.lead) {
          state.lead.contracts = state?.lead?.contracts?.map((p) =>
            p?.id === updatedContract?.id ? updatedContract : p
          )
        }
      })

      // proposal mark as sent
      .addCase(markProposalAsSent.fulfilled, (state, action) => {
        const updatedProposal = action.payload

        if (state.lead) {
          state.lead.proposals = state?.lead?.proposals?.map((p) =>
            p?.id === updatedProposal?.id ? updatedProposal : p
          )
        }
      })

      // proposal/contract email delivery also marks the document as sent
      .addCase(emailProposalToClient.fulfilled, (state, action) => {
        if (state.lead) {
          state.lead.proposals = state.lead.proposals?.map((proposal) =>
            proposal.id === action.payload.id ? action.payload : proposal
          )
        }
      })
      .addCase(emailContractToClient.fulfilled, (state, action) => {
        if (state.lead) {
          state.lead.contracts = state.lead.contracts?.map((contract) =>
            contract.id === action.payload.id ? action.payload : contract
          )
        }
      })

      .addCase(logout.fulfilled, () => {
        console.log("lead logout")
        return initialState
      })
  }
})
export const { leadSetProp } = leadSlice.actions

export default leadSlice
